/**
 * Etecplus shared parts helpers: window.EtecParts.
 * Used by the PC builder (assets/etec-pc-builder.js) and the bundle customiser
 * (assets/etec-bundle-builder.js). Load it with `defer` before either script. No dependencies.
 *
 * Holds the tag-based compatibility check (tag scheme documented at the top of
 * sections/etec-pc-builder.liquid), money formatting, and the product shape both pages use.
 */
(() => {
  if (window.EtecParts) return;

  const HEADROOM = 1.5;
  const PAGE_SIZE = 250;
  const MAX_PAGES = 20;

  /* ---------- Compatibility (pure functions) --------------------------------- */

  // Which roles are compared with each other, per tag key.
  const LINKS = {
    socket: ['cpu', 'motherboard', 'cooler'],
    ram: ['motherboard', 'ram'],
    form: ['motherboard', 'case'],
  };
  const POWER_ROLES = ['cpu', 'gpu'];
  const ALIASES = { microatx: 'matx', uatx: 'matx', miniitx: 'itx', mitx: 'itx', extendedatx: 'eatx' };

  const normaliseValue = (value) => {
    const clean = String(value)
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, '');
    return ALIASES[clean] || clean;
  };

  /** Reads `key:value` tags into { socket: [], ram: [], form: [], watts: number|null, tdp: number|null }. */
  const parseTags = (tags) => {
    const data = { socket: [], ram: [], form: [], watts: null, tdp: null };
    const list = Array.isArray(tags) ? tags : String(tags || '').split(',');
    list.forEach((raw) => {
      const tag = String(raw);
      const at = tag.indexOf(':');
      if (at < 1) return;
      const key = tag.slice(0, at).trim().toLowerCase();
      const value = tag.slice(at + 1).trim();
      if (!value) return;
      if (key === 'watts' || key === 'tdp') {
        const number = parseFloat(value.replace(/[^0-9.]/g, ''));
        if (number > 0) data[key] = Math.max(data[key] || 0, number);
      } else if (LINKS[key]) {
        const clean = normaliseValue(value);
        if (clean && !data[key].includes(clean)) data[key].push(clean);
      }
    });
    return data;
  };

  const overlaps = (a, b) => a.some((value) => b.includes(value));
  const recommendedWatts = (draw) => Math.ceil((draw * HEADROOM) / 50) * 50;

  // Total tdp of the chosen processor and graphics parts, and whether any of them has no tdp tag.
  const powerDraw = (parts) => {
    let draw = 0;
    let missing = false;
    let count = 0;
    parts.forEach((part) => {
      if (!POWER_ROLES.includes(part.role)) return;
      count += 1;
      if (part.data.tdp) draw += part.data.tdp * (part.qty || 1);
      else missing = true;
    });
    return { draw, missing, count };
  };

  /**
   * Checks one candidate product against the parts already in the build.
   * candidate: { role, data }   parts: [{ role, title, data, qty }]
   * Returns { status: 'none'|'compatible'|'unknown'|'warning'|'incompatible', conflicts, matches, unknown, power }.
   */
  const checkProduct = (candidate, parts) => {
    const result = { status: 'none', conflicts: [], matches: [], unknown: [], power: null };

    Object.keys(LINKS).forEach((key) => {
      if (!LINKS[key].includes(candidate.role)) return;
      parts.forEach((part) => {
        if (part.role === candidate.role || !LINKS[key].includes(part.role)) return;
        const mine = candidate.data[key];
        const theirs = part.data[key];
        const entry = { key, title: part.title, role: part.role };
        if (!mine.length || !theirs.length) result.unknown.push(entry);
        else if (overlaps(mine, theirs)) result.matches.push(entry);
        else result.conflicts.push(entry);
      });
    });

    if (candidate.role === 'psu') {
      const power = powerDraw(parts);
      if (power.count) {
        if (candidate.data.watts && power.draw > 0) {
          const low = candidate.data.watts < power.draw * HEADROOM;
          result.power = {
            low,
            watts: candidate.data.watts,
            draw: power.draw,
            recommended: recommendedWatts(power.draw),
          };
          if (!low && !power.missing) result.matches.push({ key: 'watts', title: '', role: 'power' });
          if (power.missing) result.unknown.push({ key: 'watts', title: '', role: 'power' });
        } else {
          result.unknown.push({ key: 'watts', title: '', role: 'power' });
        }
      }
    }

    if (result.conflicts.length) result.status = 'incompatible';
    else if (result.power && result.power.low) result.status = 'warning';
    else if (result.unknown.length) result.status = 'unknown';
    else if (result.matches.length) result.status = 'compatible';
    return result;
  };

  /**
   * Checks the whole build. parts: [{ role, title, data, qty }]
   * Returns { issues: [{ a, b, key }], warnings: [{ part, watts, draw, recommended }], unknown: boolean }.
   */
  const evaluateBuild = (parts) => {
    const result = { issues: [], warnings: [], unknown: false };

    for (let i = 0; i < parts.length; i += 1) {
      for (let j = i + 1; j < parts.length; j += 1) {
        const a = parts[i];
        const b = parts[j];
        if (a.role === b.role) continue;
        Object.keys(LINKS).forEach((key) => {
          if (!LINKS[key].includes(a.role) || !LINKS[key].includes(b.role)) return;
          if (!a.data[key].length || !b.data[key].length) result.unknown = true;
          else if (!overlaps(a.data[key], b.data[key])) result.issues.push({ a: a.title, b: b.title, key });
        });
      }
    }

    const power = powerDraw(parts);
    parts
      .filter((part) => part.role === 'psu')
      .forEach((psu) => {
        if (!power.count) return;
        if (!psu.data.watts || power.draw <= 0) {
          result.unknown = true;
          return;
        }
        if (power.missing) result.unknown = true;
        if (psu.data.watts < power.draw * HEADROOM) {
          result.warnings.push({
            part: psu.title,
            watts: psu.data.watts,
            draw: power.draw,
            recommended: recommendedWatts(power.draw),
          });
        }
      });

    return result;
  };

  /* ---------- Helpers ---------------------------------------------------------- */

  const el = (tag, props, ...children) => {
    const node = document.createElement(tag);
    Object.entries(props || {}).forEach(([key, value]) => {
      if (value === false || value == null) return;
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else if (key === 'data') Object.assign(node.dataset, value);
      else if (value === true) node.setAttribute(key, '');
      else node.setAttribute(key, value);
    });
    children.flat().forEach((child) => {
      if (child != null && child !== false) node.append(child);
    });
    return node;
  };

  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const toCents = (value) => {
    if (value == null || value === '') return null;
    const number = Math.round(parseFloat(value) * 100);
    return Number.isFinite(number) ? number : null;
  };

  const formatMoney = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (error) {
        /* fall through to the local formatter */
      }
    }
    const withDelimiters = (number, precision, thousands, decimal) => {
      const parts = (number / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const template = format && pattern.test(format) ? format : '${{amount}}';
    let value;
    switch (template.match(pattern)[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return template.replace(pattern, value);
  };

  // The money format can contain markup (for example a currency span); show it as text only.
  const moneyText = (cents, format) => {
    const holder = document.createElement('div');
    holder.innerHTML = formatMoney(cents, format);
    return holder.textContent;
  };

  const sizedImage = (src, width) => {
    if (!src) return null;
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  // The SKU is the manufacturer part number. A product tagged "hide-sku" still carries a supplier's
  // own code as its SKU (no usable part number), so its SKU is neither shown nor searched.
  const hasHideSkuTag = (tags) =>
    (Array.isArray(tags) ? tags : String(tags || '').split(','))
      .some((tag) => String(tag).trim().toLowerCase() === 'hide-sku');

  const normaliseProduct = (raw) => {
    const hideSku = hasHideSkuTag(raw.tags);
    const variants = (raw.variants || []).map((variant) => ({
      id: variant.id,
      title: variant.title,
      sku: hideSku ? '' : variant.sku || '',
      price: toCents(variant.price) || 0,
      compareAt: toCents(variant.compare_at_price),
      available: Boolean(variant.available),
      image: variant.featured_image ? variant.featured_image.src : null,
    }));
    const image = raw.images && raw.images[0] ? raw.images[0].src : null;
    const prices = variants.filter((variant) => variant.available).map((variant) => variant.price);
    const allPrices = variants.map((variant) => variant.price);
    return {
      id: raw.id,
      title: raw.title || '',
      handle: raw.handle,
      vendor: raw.vendor || '',
      url: `${rootUrl()}products/${raw.handle}`,
      image,
      variants,
      available: variants.some((variant) => variant.available),
      sortPrice: prices.length ? Math.min(...prices) : allPrices.length ? Math.min(...allPrices) : 0,
      data: parseTags(raw.tags),
      search: [raw.title, raw.vendor, ...variants.map((variant) => variant.sku)].join(' ').toLowerCase(),
    };
  };

  /* ---------- Loading a collection ------------------------------------------- */

  /**
   * Loads every product in a collection from /collections/<handle>/products.json, normalised.
   * Stops after MAX_PAGES pages of PAGE_SIZE. Pass an AbortSignal to cancel.
   */
  const fetchCollection = async (handle, signal) => {
    const base = `${rootUrl()}collections/${encodeURIComponent(handle)}/products.json?limit=${PAGE_SIZE}`;
    const products = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const response = await fetch(`${base}&page=${page}`, { signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Could not load ${handle} (${response.status})`);
      const batch = (await response.json()).products || [];
      batch.forEach((raw) => products.push(normaliseProduct(raw)));
      if (batch.length < PAGE_SIZE) break;
    }
    return products;
  };

  window.EtecParts = {
    HEADROOM,
    LINKS,
    parseTags,
    checkProduct,
    evaluateBuild,
    recommendedWatts,
    el,
    fill,
    toCents,
    formatMoney,
    moneyText,
    sizedImage,
    rootUrl,
    normaliseProduct,
    fetchCollection,
  };
})();
